import React, { useState, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { AdminUser } from '../utils/api';
import {
  HomeIcon,
  UsersIcon,
  ComputerDesktopIcon,
  DocumentTextIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  DevicePhoneMobileIcon,
  ServerIcon,
  EnvelopeIcon,
  ChartBarIcon,
  UserCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

interface AdminLayoutProps {
  children: ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const { admin, logout } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: HomeIcon, current: location.pathname === '/admin' },
    { name: 'User Analytics', href: '/admin/user-analytics', icon: ChartBarIcon, current: location.pathname === '/admin/user-analytics' },
    { name: 'Users', href: '/admin/users', icon: UsersIcon, current: location.pathname === '/admin/users' },
    { name: 'Devices', href: '/admin/devices', icon: DevicePhoneMobileIcon, current: location.pathname === '/admin/devices' },
    { name: 'Sessions', href: '/admin/sessions', icon: ComputerDesktopIcon, current: location.pathname === '/admin/sessions' },
    { name: 'Connection Nodes', href: '/admin/connection-nodes', icon: ServerIcon, current: location.pathname === '/admin/connection-nodes' },
    { name: 'Email Queue', href: '/admin/email-queue', icon: EnvelopeIcon, current: location.pathname === '/admin/email-queue' },
    { name: 'Email Analytics', href: '/admin/email-analytics', icon: ChartBarIcon, current: location.pathname === '/admin/email-analytics' },
    { name: 'Audit Logs', href: '/admin/logs', icon: DocumentTextIcon, current: location.pathname === '/admin/logs' },
    { name: 'Admins', href: '/admin/admins', icon: ShieldCheckIcon, current: location.pathname === '/admin/admins' },
    { name: 'Profile', href: '/admin/profile', icon: UserCircleIcon, current: location.pathname === '/admin/profile' },
    { name: 'System', href: '/admin/system', icon: CogIcon, current: location.pathname === '/admin/system' },
  ];

  const getRoleDisplay = (role: string) => {
    const roleMap = {
      'super_admin': 'Super Admin',
      'admin': 'Admin',
      'support': 'Support',
      'readonly': 'Read Only'
    };
    return roleMap[role as keyof typeof roleMap] || role;
  };

  const getRoleBadgeColor = (role: string) => {
    const colorMap = {
      'super_admin': 'bg-purple-100 text-purple-800',
      'admin': 'bg-blue-100 text-blue-800',
      'support': 'bg-green-100 text-green-800',
      'readonly': 'bg-gray-100 text-gray-800'
    };
    return colorMap[role as keyof typeof colorMap] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Sidebar for mobile */}
      <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <SidebarContent 
            navigation={navigation} 
            admin={admin} 
            getRoleDisplay={getRoleDisplay}
            getRoleBadgeColor={getRoleBadgeColor}
            handleLogout={handleLogout}
          />
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <SidebarContent 
            navigation={navigation} 
            admin={admin} 
            getRoleDisplay={getRoleDisplay}
            getRoleBadgeColor={getRoleBadgeColor}
            handleLogout={handleLogout}
          />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top bar */}
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <button
            type="button"
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex items-center">
              <h1 className="text-2xl font-semibold text-gray-900">
                Fawayo Admin
              </h1>
            </div>
            
            <div className="ml-4 flex items-center md:ml-6">
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-700">
                  Welcome, {admin?.display_name}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(admin?.role || '')}`}>
                  {getRoleDisplay(admin?.role || '')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Main content area */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

interface SidebarContentProps {
  navigation: Array<{
    name: string;
    href: string;
    icon: React.ComponentType<{ className?: string }>;
    current: boolean;
  }>;
  admin: AdminUser | null;
  getRoleDisplay: (role: string) => string;
  getRoleBadgeColor: (role: string) => string;
  handleLogout: () => void;
}

const SidebarContent: React.FC<SidebarContentProps> = ({
  navigation,
  admin,
  getRoleDisplay,
  getRoleBadgeColor,
  handleLogout
}) => {
  return (
    <div className="flex flex-col h-0 flex-1 border-r border-gray-200 bg-white">
      {/* Logo */}
      <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
        <div className="flex items-center flex-shrink-0 px-4">
          <img src="/logo.svg" alt="" className="h-8 w-8" />
          <span className="ml-2 text-xl font-bold text-gray-900">Fawayo Admin</span>
        </div>
        
        {/* Navigation */}
        <nav className="mt-8 flex-1 px-2 bg-white space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`${
                  item.current
                    ? 'bg-blue-100 border-blue-500 text-blue-700 border-r-2'
                    : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                } group flex items-center px-2 py-2 text-sm font-medium border-l-4 transition-colors duration-200`}
              >
                <Icon className="mr-3 h-6 w-6" />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </div>
      
      {/* User info and logout */}
      <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
        <div className="flex-shrink-0 w-full group block">
          <div className="flex items-center">
            <div className="inline-block h-10 w-10 rounded-full bg-blue-600 flex items-center justify-center">
              <span className="text-sm font-medium leading-none text-white">
                {admin?.display_name?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium text-gray-700 truncate">
                {admin?.display_name}
              </p>
              <div className="flex items-center justify-between">
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(admin?.role || '')}`}>
                  {getRoleDisplay(admin?.role || '')}
                </span>
                <button
                  onClick={handleLogout}
                  className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                  title="Logout"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;